import {
    SpaceBetween,
    BreadcrumbGroup,
    Header,
    Box,
    Table,
    Button,
    TextFilter,
    Pagination,
    CollectionPreferences,
    Flashbar,
} from '@amzn/awsui-components-console';
import React, { useEffect, useState } from 'react';
import { fetchAssets, deleteAsset } from './apis';
import * as Constants from './constants';
import * as GlobalConstants from '../../lib/constants';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { EmptyState, getFilterCounterText } from '../../lab-management/group/utils';
import { getCustomTags } from './utils';
import { useLabAuth } from '../../contexts/LabAuthContext';
import LabPermissionDenied from '../../auth/LabPermissionDenied';
import { LoadingSpinner } from '../../lib/utils';

/**
 * Asset manager component
 */
export default function AssetsManager() {
    const { isLabAdmin, isLoading } = useLabAuth();
    const [selectedAsset, setSelectedAsset] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [assets, setAssets] = React.useState([]);
    const [deleteDisabled, setDeleteDisabled] = React.useState(true);
    const [preferences, setPreferences] = useState({
        pageSize: 20,
        stickyColumns: { first: 1 },
        visibleContent: Constants.ASSET_VISIBLE_COLUMNS,
    });
    const { items, actions, collectionProps, filteredItemsCount, filterProps, paginationProps } = useCollection(
        assets,
        {
            filtering: {
                noMatch: (
                    <EmptyState
                        title="No matches"
                        subtitle="We can’t find a match."
                        action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
                    />
                ),
            },
            pagination: { pageSize: preferences.pageSize },
            sorting: {
                defaultState: { sortingColumn: Constants.SORTED_FILED, isDescending: true },
            },
            selection: {},
        }
    );
    const [successMessage, setSuccessMessage] = React.useState([]);
    const [errorMessage, setErrorMessage] = React.useState([]);

    /**
     * process selection change event
     */
    const onSelectionChange = (selectedItems) => {
        // when asset is selected
        setSelectedAsset(selectedItems);
        setDeleteDisabled(false);
    };
    const startLoading = () => {
        // initialize loading status
        setLoading(true);
        setDeleteDisabled(true);
    };
    const endLoading = () => {
        // end loading status
        setLoading(false);
    };

    const displayErrorContent = (content) => {
        // display error message
        setErrorMessage([
            {
                type: 'error',
                dismissible: true,
                dismissLabel: 'Dismiss message',
                onDismiss: () => setErrorMessage([]),
                content: <>{content}</>,
                action: (
                    <Button href={GlobalConstants.CONTACT_US} iconAlign="right" target="_blank">
                        Contact us
                    </Button>
                ),
            },
        ]);
    };
    const displaySuccessContent = (content) => {
        // display success message
        setSuccessMessage([
            {
                type: 'success',
                dismissible: true,
                dismissLabel: 'Dismiss message',
                onDismiss: () => setSuccessMessage([]),
                content: <>{content}</>,
            },
        ]);
    };

    /**
     * load asset table
     */
    const loadTable = () => {
        (async function () {
            startLoading();
            try {
                const response = await fetchAssets();
                const assets = response['response'];
                assets.forEach((item) => {
                    item['tags'] = getCustomTags(item['tags']);
                });
                setAssets(assets);
            } catch (e) {
                console.error('Fail to load asset(s) with error', e);
            }
            endLoading();
        })();
    };

    const onSubmitDelete = () => {
        // when delete button is triggered
        (async function () {
            try {
                const assetToDeleteID = selectedAsset[0][Constants.id];
                await deleteAsset(assetToDeleteID);
                loadTable();
                displaySuccessContent(`Successfully delete the selected asset with id ${assetToDeleteID}`);
            } catch (e) {
                console.error('Fail to delete selected asset with error', e);
                displayErrorContent(
                    'Cannot delete user token. Contact our dev team @ds2-device-farm-dev for more details.'
                );
            }
        })();
    };

    useEffect(() => {
        // initialize the component
        if (isLabAdmin) {
            loadTable();
        }
    }, [isLabAdmin]);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (!isLabAdmin) {
        return <LabPermissionDenied />;
    }

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#' },
                    { text: 'Asset management', href: '#/office-lab/assets' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <Header variant="h1" description="This page shows all DDF asset(s) that you are allowed to view and edit.">
                Welcome to DS2 Device Farm - Assets management
            </Header>
            <Table
                {...collectionProps}
                onSelectionChange={({ detail }) => onSelectionChange(detail.selectedItems)}
                selectedItems={selectedAsset}
                selectionType="single"
                header={
                    <Header
                        counter={`(${assets.length})`}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button onClick={loadTable} iconName="refresh" variant="icon" />
                                <Button disabled={deleteDisabled} onClick={onSubmitDelete}>
                                    Delete
                                </Button>
                                <Button href="#/office-lab/assets/create" variant="primary">
                                    Create
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        DDF assets
                    </Header>
                }
                columnDefinitions={Constants.ASSET_COLUMNS_DEFINITION}
                visibleColumns={preferences.visibleContent}
                items={items}
                pagination={<Pagination {...paginationProps} />}
                filter={<TextFilter {...filterProps} countText={getFilterCounterText(filteredItemsCount)} />}
                empty={
                    <Box textAlign="center" color="inherit">
                        <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                            No assets to display
                        </Box>
                    </Box>
                }
                loading={loading}
                loadingText="Loading assets"
                stickyColumns={preferences.stickyColumns}
                resizableColumns
                preferences={
                    <CollectionPreferences
                        {...Constants.COLLECTION_PREFERENCES_PROPS}
                        preferences={preferences}
                        onConfirm={({ detail }) => setPreferences(detail)}
                    />
                }
            />
            <Flashbar items={errorMessage} />
            <Flashbar items={successMessage} />
        </SpaceBetween>
    );
}
